// Patch theme.pubsub to prevent bundle variant pickers from triggering main product updates
(function() {
  if (!window.theme || !window.theme.pubsub) {
    console.warn('theme.pubsub not available yet, bundle isolation may not work');
    return;
  }

  const originalPublish = window.theme.pubsub.publish;

  window.theme.pubsub.publish = function(event, data) {
    // Check if this is a variant change event from a bundle product
    if (event === window.theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange) {
      const eventData = data?.data?.event;
      const target = eventData?.target;

      // Check if the event originated from within a bundle form or product-builder
      if (target && target.closest) {
        const bundleForm = target.closest('product-bundle-form');
        const bundleBuilder = target.closest('[data-product-builder="true"]');
        const kitBuilder = target.closest('[data-kit-builder="true"]');

        if (bundleForm || bundleBuilder || kitBuilder) {
          // Don't publish - this is a bundle/kit variant change, not main product
          return;
        }
      }
    }

    // Otherwise, call the original publish function
    return originalPublish.apply(this, arguments);
  };
})();

class BundleVariantSelector {
  constructor() {
    this.cards = Array.from(document.querySelectorAll('.product-builder__card'));
    if (!this.cards.length) return;

    this.helpDrawerInitialised = false;
    this.builderRoot = this.cards[0]?.closest('[data-product-builder="true"]')
      || document.querySelector('[data-product-builder="true"]');
    this.productFormId = this.builderRoot?.getAttribute('form') || null;
    this.form = this.productFormId
      ? document.getElementById(this.productFormId)
      : document.querySelector('form[action$="/cart/add"]');

    const submitSelector = this.productFormId
      ? `.product-form__submit[form="${this.productFormId}"]`
      : '.product-form__submit';

    this.submitButton = this.form?.querySelector(submitSelector)
      || document.querySelector(submitSelector)
      || this.form?.querySelector('.product-form__submit');

    this.errorMessage = this.form?.querySelector('.product-form__error-message')
      || (this.productFormId ? document.querySelector(`#${this.productFormId} .product-form__error-message`) : null)
      || null;

    this.requiredButtonText = this.builderRoot?.dataset.requiredButtonText || '';
    this.reverseRequiredMatch = (this.builderRoot?.dataset.reverseRequiredMatch || '').trim();
    this.reversedButtonText = (this.builderRoot?.dataset.reversedButtonText || '').trim();
    this.reversedHeading = (this.builderRoot?.dataset.reversedHeading || '').trim();
    this.lastReversedState = null;
    this.submitLabel = this.submitButton?.querySelector('.btn-text') || null;

    this.productInfo = this.builderRoot?.closest('product-info') || null;
    this.primaryPriceWrapper = this.productInfo?.querySelector('[id^="Price-"][id*="main-product"] .price') || null;
    if (this.primaryPriceWrapper) {
      this.primaryPriceOriginalHtml = this.primaryPriceWrapper.innerHTML;
      this.primaryPriceOriginalClass = this.primaryPriceWrapper.className;
    }

    this.conditionalBlocks = Array.from((this.productInfo || document).querySelectorAll('[data-required-variant]'));

    this.init();
  }

  init() {
    this.cards.forEach((card) => this.setupCard(card));
    this.bindEvents();
    this.setupHelpDrawer();
    this.attachFormGuard();

    this.waitForElement('#Product-Form-main .price .money, form[action$="/cart/add"] .price .money', () => {
      this.updateCustomTotalPrice();
      this.updateConditionalBlocks();
    });

    const handleMainVariantChange = () => {
      setTimeout(() => {
        if (this.primaryPriceWrapper) {
          this.primaryPriceOriginalHtml = this.primaryPriceWrapper.innerHTML;
          this.primaryPriceOriginalClass = this.primaryPriceWrapper.className;
        }
        this.updateCustomTotalPrice();
        this.updateConditionalBlocks();
      }, 50);
      this.updateReversedState();
      this.updateAddToCartState();
    };

    const mainProductForm = this.form || document.querySelector('form[action$="/cart/add"]');
    if (mainProductForm) {
      mainProductForm.addEventListener('variant:change', handleMainVariantChange);
    }

    // The theme dispatches variant:change on the <product-form> element without
    // bubbling, so the form listener above never fires here. The pubsub
    // variantChange event is the reliable main-product signal.
    if (window.theme?.pubsub) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.variantChange, handleMainVariantChange);
    }

    this.updateReversedState();
    this.updateAddToCartState();
    this.updateConditionalBlocks();
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const action = event.target.closest('[data-bundle-action]');
      if (action) {
        event.preventDefault();
        const card = action.closest('.product-builder__card');
        if (!card) return;

        switch (action.dataset.bundleAction) {
          case 'toggle':
            this.toggleOptions(card);
            break;
          case 'apply':
            this.applySelection(card);
            break;
          case 'cancel':
            this.cancelSelection(card);
            break;
          case 'remove':
            this.removeSelection(card);
            break;
          default:
            break;
        }
        return;
      }

      const selectButton = event.target.closest('.product-builder__select-btn');
      if (selectButton) {
        event.preventDefault();
        const { productId, variantId } = selectButton.dataset;
        this.selectSingleVariant(productId, variantId, selectButton);
        return;
      }

      const helpDrawerTrigger = event.target.closest('[aria-controls=\"HelpDrawer\"]');
      if (helpDrawerTrigger) {
        this.setupHelpDrawer();

        if (helpDrawerTrigger.matches('.product-builder__help-link[data-bundle-help=\"drawer\"]')) {
          this.applyHelpDrawerContent(helpDrawerTrigger);
        } else {
          this.resetHelpDrawerContent();
        }
      }
    });

    document.addEventListener('change', (event) => {
      const pickerInput = event.target.closest('variant-picker input[type="radio"], variant-picker select');
      if (pickerInput) {
        const card = pickerInput.closest('.product-builder__card');
        if (!card) return;

        const variant = this.getSelectedVariantFromPicker(card);
        if (variant) {
          this.updateOptionsPreview(card, variant);
        }
        this.updateApplyButtonState(card, variant);
      }
    });
  }

  setupCard(card) {
    const hiddenInput = card.querySelector('.product-builder__input');
    card.dataset.originalVariantId = card.dataset.currentVariantId || hiddenInput?.value || '';

    if (hiddenInput) {
      hiddenInput.addEventListener('change', () => {
        this.updateAddToCartState();
      });
    }

    const variantText = card.querySelector('.product-builder__variant');
    if (variantText) {
      card.dataset.originalVariantTitle = variantText.textContent.trim();
    }

    const priceDiv = card.querySelector('.product-builder__price');
    if (priceDiv) {
      card.dataset.originalPrice = priceDiv.innerHTML;
    }

    if (hiddenInput) {
      card.dataset.originalInputPrice = hiddenInput.dataset.price || hiddenInput.getAttribute('data-price') || '0';
    }

    const image = card.querySelector('.product-builder__image img');
    if (image) {
      image.dataset.originalSrc = image.getAttribute('src') || image.currentSrc || '';
      image.dataset.originalSrcset = image.getAttribute('srcset') || '';
      image.dataset.originalAlt = image.getAttribute('alt') || '';
    }

    const variantsScript = card.querySelector('[data-product-variants]');
    if (variantsScript) {
      try {
        card._productVariants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        card._productVariants = [];
      }
    }

    const initialVariantId = card.dataset.currentVariantId || card.dataset.originalVariantId;
    const initialVariant = this.getVariantById(card, initialVariantId);
    if (initialVariant) {
      this.updateOptionsPreview(card, initialVariant);
      this.updateApplyButtonState(card, initialVariant);
    } else {
      this.updateApplyButtonState(card, null);
    }

    this.setCardSelectedState(card, hiddenInput?.checked);
  }

  setupHelpDrawer() {
    if (this.helpDrawerInitialised) return;

    const drawer = document.getElementById('HelpDrawer');
    if (!drawer) return;

    this.helpDrawer = drawer;
    this.helpDrawerDynamic = drawer.querySelector('[data-help-drawer-dynamic]');
    this.helpDrawerStatic = drawer.querySelector('[data-help-drawer-static]');
    this.helpDrawerTitle = drawer.querySelector('[data-help-drawer-title]');

    if (this.helpDrawerTitle && !this.helpDrawerTitle.dataset.defaultTitle) {
      this.helpDrawerTitle.dataset.defaultTitle = this.helpDrawerTitle.textContent.trim();
    }

    drawer.addEventListener('drawer:afterHide', () => {
      this.resetHelpDrawerContent();
    });

    this.helpDrawerInitialised = true;
  }

  resetHelpDrawerContent() {
    if (!this.helpDrawerDynamic || !this.helpDrawerStatic) return;

    this.helpDrawerDynamic.innerHTML = '';
    this.helpDrawerDynamic.hidden = true;
    this.helpDrawerDynamic.classList.add('hidden');

    this.helpDrawerStatic.hidden = false;
    this.helpDrawerStatic.classList.remove('hidden');

    if (this.helpDrawerTitle && this.helpDrawerTitle.dataset.defaultTitle) {
      this.helpDrawerTitle.textContent = this.helpDrawerTitle.dataset.defaultTitle;
    }
  }

  applyHelpDrawerContent(button) {
    if (!this.helpDrawerInitialised) {
      this.setupHelpDrawer();
    }

    if (!this.helpDrawerDynamic || !this.helpDrawerStatic) return;

    const templateId = button.dataset.helpTemplateId;
    const template = templateId ? document.getElementById(templateId) : null;

    if (template) {
      const templateHtml = template.innerHTML.trim();
      if (templateHtml.length > 0) {
        this.helpDrawerDynamic.innerHTML = '';
        this.helpDrawerDynamic.appendChild(template.content.cloneNode(true));
        this.helpDrawerDynamic.hidden = false;
        this.helpDrawerDynamic.classList.remove('hidden');

        this.helpDrawerStatic.hidden = true;
        this.helpDrawerStatic.classList.add('hidden');

        if (this.helpDrawerTitle) {
          if (!this.helpDrawerTitle.dataset.defaultTitle) {
            this.helpDrawerTitle.dataset.defaultTitle = this.helpDrawerTitle.textContent.trim();
          }
          const heading = button.dataset.helpTitle;
          if (heading) {
            this.helpDrawerTitle.textContent = heading;
          } else if (this.helpDrawerTitle.dataset.defaultTitle) {
            this.helpDrawerTitle.textContent = this.helpDrawerTitle.dataset.defaultTitle;
          }
        }

        return;
      }
    }

    this.resetHelpDrawerContent();
  }

  toggleOptions(card) {
    const options = card.querySelector('[data-product-builder-options]');
    if (!options) return;

    if (options.hasAttribute('hidden')) {
      this.openOptions(card, options);
    } else {
      this.closeOptions(card, options);
    }
  }

  openOptions(card, options) {
    this.cards.forEach((otherCard) => {
      if (otherCard !== card) {
        const otherOptions = otherCard.querySelector('[data-product-builder-options]');
        if (otherOptions && !otherOptions.hasAttribute('hidden')) {
          this.closeOptions(otherCard, otherOptions);
        }
      }
    });

    card.classList.remove('is-closing');
    options.classList.remove('is-closing');

    if (options._closeTimer) {
      clearTimeout(options._closeTimer);
      options._closeTimer = null;
    }

    if (options._onTransitionEnd) {
      options.removeEventListener('transitionend', options._onTransitionEnd);
      options._onTransitionEnd = null;
    }

    this.syncOptionsWithCurrentVariant(card);
    options.hidden = false;

    const targetHeight = options.scrollHeight;
    options.style.height = '0px';
    options.style.opacity = '0';
    options.style.transform = 'translateY(-12px)';

    requestAnimationFrame(() => {
      card.classList.add('is-open');
      options.style.height = `${targetHeight}px`;
      options.style.opacity = '1';
      options.style.transform = 'translateY(0)';
    });

    const handleOpenEnd = (event) => {
      if (event.target !== options || event.propertyName !== 'height') return;
      options.style.height = 'auto';
      options.style.opacity = '';
      options.style.transform = '';
      options.removeEventListener('transitionend', handleOpenEnd);
    };
    options.addEventListener('transitionend', handleOpenEnd);

    const currentVariant = this.getVariantById(card, card.dataset.currentVariantId || card.dataset.originalVariantId);
    if (currentVariant) {
      this.updateOptionsPreview(card, currentVariant);
      this.updateApplyButtonState(card, currentVariant);
    } else {
      this.updateApplyButtonState(card, null);
    }

    this.updateToggleButtonState(card, true);

    const firstAvailable = options.querySelector('variant-picker input[type="radio"]:not(:disabled)');
    firstAvailable?.focus({ preventScroll: true });
  }

  closeOptions(card, options, shouldScroll = true) {
    if (!options) return;

    if (options.hidden) {
      card.classList.remove('is-open');
      card.classList.remove('is-closing');
      options.classList.remove('is-closing');
      this.updateToggleButtonState(card, false);
      return;
    }

    card.classList.add('is-closing');
    options.classList.add('is-closing');
    card.classList.remove('is-open');
    this.updateToggleButtonState(card, false);

    const currentHeight = options.scrollHeight;

    // First, set the starting state without transition
    options.style.transition = 'none';
    options.style.height = `${currentHeight}px`;
    options.style.opacity = '1';
    options.style.transform = 'translateY(0)';

    // Force a reflow to ensure the browser registers the starting state
    void options.offsetHeight;

    // Then enable transitions
    options.style.transition = 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)';

    // Use a small timeout to ensure transition is applied before changing values
    setTimeout(() => {
      options.style.height = '0px';
      options.style.opacity = '0';
      options.style.transform = 'translateY(-12px)';
    }, 20);

    const finalizeClose = () => {
      if (options._closeTimer) {
        clearTimeout(options._closeTimer);
        options._closeTimer = null;
      }
      options.hidden = true;
      options.classList.remove('is-closing');
      card.classList.remove('is-closing');
      options.style.height = '';
      options.style.opacity = '';
      options.style.transform = '';
      options.style.transition = '';
      options._onTransitionEnd = null;

      // Only scroll if explicitly requested (e.g., when clicking cancel button)
      if (shouldScroll) {
        this.scrollToCardHeader(card);
      }
    };

    const onTransitionEnd = (event) => {
      if (event.target !== options || event.propertyName !== 'height') return;
      options.removeEventListener('transitionend', onTransitionEnd);
      finalizeClose();
    };

    options._onTransitionEnd = onTransitionEnd;
    options.addEventListener('transitionend', onTransitionEnd, { once: true });
    options._closeTimer = setTimeout(() => {
      if (!options.hidden) {
        options.removeEventListener('transitionend', onTransitionEnd);
        finalizeClose();
      }
    }, 900); // Increased from 700ms to 900ms to match slower animation (0.8s + buffer)
  }

  scrollToCardHeader(card, callback) {
    if (!card) return;

    // Get the header height if there's a sticky header
    let headerOffset = 0;
    const stickyHeader = document.querySelector('header.header');
    if (stickyHeader) {
      headerOffset = stickyHeader.offsetHeight + 20; // Add some padding
    }

    const cardTop = card.getBoundingClientRect().top + window.scrollY;
    const scrollToPosition = cardTop - headerOffset;

    // If there's a callback and smooth scrolling, wait for scroll to complete
    if (callback && !theme?.config?.motionReduced) {
      // Calculate approximate scroll duration based on distance
      const scrollDistance = Math.abs(window.scrollY - scrollToPosition);
      const scrollDuration = Math.min(1000, scrollDistance / 2); // Max 1 second

      window.scrollTo({
        top: scrollToPosition,
        behavior: 'smooth'
      });

      // Execute callback after scroll completes
      setTimeout(callback, scrollDuration);
    } else {
      window.scrollTo({
        top: scrollToPosition,
        behavior: theme?.config?.motionReduced ? 'auto' : 'smooth'
      });

      // If motion is reduced, callback executes immediately
      if (callback && theme?.config?.motionReduced) {
        callback();
      }
    }
  }

  updateToggleButtonState(card, isOpen) {
    const toggleButtons = card.querySelectorAll('[data-bundle-action="toggle"]');
    toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  syncOptionsWithCurrentVariant(card) {
    const options = card.querySelector('[data-product-builder-options]');
    if (!options) return;
    const picker = options.querySelector('variant-picker');
    if (!picker) return;

    const currentVariant = this.getVariantById(card, card.dataset.currentVariantId || card.dataset.originalVariantId);

    const fieldsets = picker.querySelectorAll('fieldset');
    fieldsets.forEach((fieldset) => {
      const optionKey = fieldset.dataset.optionIndex || '';
      const position = parseInt(optionKey.replace('option', ''), 10);
      const desiredValue = currentVariant ? currentVariant[`option${position}`] : null;

      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      let matched = false;
      inputs.forEach((input) => {
        if (desiredValue) {
          input.checked = input.value === desiredValue;
        } else if (!matched && !input.disabled) {
          input.checked = true;
          matched = true;
        } else if (!desiredValue) {
          input.checked = false;
        }
      });
    });

    const selects = picker.querySelectorAll('select');
    selects.forEach((select) => {
      const optionKey = select.dataset.optionIndex || select.name;
      const position = parseInt(optionKey?.replace('option', ''), 10);
      const desiredValue = currentVariant ? currentVariant[`option${position}`] : null;
      if (!desiredValue) return;
      Array.from(select.options).forEach((option) => {
        option.selected = option.value === desiredValue;
      });
    });
  }

  applySelection(card) {
    const variant = this.getSelectedVariantFromPicker(card);
    if (!variant) return;
    if (variant.available === false) {
      this.updateApplyButtonState(card, variant);
      return;
    }

    const variantData = this.normalizeVariantData(variant);
    const hiddenInput = card.querySelector('.product-builder__input');
    if (!hiddenInput) return;

    hiddenInput.value = variantData.id;
    hiddenInput.dataset.price = String(variantData.price);
    hiddenInput.checked = true;
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    card.dataset.currentVariantId = String(variantData.id);

    this.updateCardSummary(card, variantData);
    this.setCardSelectedState(card, true);

    this.updateOptionsPreview(card, variant);
    this.updateApplyButtonState(card, variant);

    this.triggerBundlePriceUpdate();
    this.updateCustomTotalPrice();
    this.updateConditionalBlocks();
    this.updateAddToCartState();

    // Scroll first, then collapse after scroll completes
    const options = card.querySelector('[data-product-builder-options]');
    if (options) {
      this.scrollToCardHeader(card, () => {
        // Callback executes after scroll completes
        this.closeOptions(card, options, false); // Pass false to skip the scroll
      });
    }
  }

  cancelSelection(card) {
    const options = card.querySelector('[data-product-builder-options]');
    if (!options) return;

    this.syncOptionsWithCurrentVariant(card);
    const currentVariant = this.getVariantById(card, card.dataset.currentVariantId || card.dataset.originalVariantId);
    if (currentVariant) {
      this.updateOptionsPreview(card, currentVariant);
      this.updateApplyButtonState(card, currentVariant);
    } else {
      this.updateApplyButtonState(card, null);
    }
    this.closeOptions(card, options);
  }

  removeSelection(card) {
    const hiddenInput = card.querySelector('.product-builder__input');
    if (!hiddenInput) return;

    hiddenInput.checked = false;
    hiddenInput.value = card.dataset.originalVariantId || hiddenInput.value;
    hiddenInput.dataset.price = card.dataset.originalInputPrice || hiddenInput.dataset.price;
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    card.dataset.currentVariantId = card.dataset.originalVariantId || '';
    this.resetCardSummary(card);
    this.setCardSelectedState(card, false);
    this.syncOptionsWithCurrentVariant(card);
    const originalVariant = this.getVariantById(card, card.dataset.originalVariantId);
    if (originalVariant) {
      this.updateOptionsPreview(card, originalVariant);
      this.updateApplyButtonState(card, originalVariant);
    } else {
      this.updateApplyButtonState(card, null);
    }

    const options = card.querySelector('[data-product-builder-options]');
    if (options && !options.hasAttribute('hidden')) {
      this.closeOptions(card, options);
    }

    this.triggerBundlePriceUpdate();
    this.updateCustomTotalPrice();
    this.updateConditionalBlocks();
    this.updateAddToCartState();
  }

  selectSingleVariant(productId, variantId, button) {
    const card = button.closest('.product-builder__card') || this.cards.find((c) => c.dataset.productId === productId);
    if (!card) return;

    const hiddenInput = card.querySelector('.product-builder__input');
    if (!hiddenInput) return;

    hiddenInput.value = variantId;
    hiddenInput.checked = true;
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    card.dataset.currentVariantId = variantId;
    const variant = this.getVariantById(card, variantId);
    if (variant) {
      this.updateCardSummary(card, this.normalizeVariantData(variant));
    }
    this.setCardSelectedState(card, true);

    this.triggerBundlePriceUpdate();
    this.updateCustomTotalPrice();
    this.updateConditionalBlocks();
    this.updateAddToCartState();
  }

  setCardSelectedState(card, isSelected) {
    const viewButton = card.querySelector('.product-builder__view-btn');
    const editButton = card.querySelector('.product-builder__edit-btn');
    const removeButton = card.querySelector('.product-builder__remove-btn');
    const selectButton = card.querySelector('.product-builder__select-btn');

    if (isSelected) {
      card.classList.add('selected');
      viewButton?.classList.add('hidden');
      editButton?.classList.remove('hidden');
      removeButton?.classList.remove('hidden');
      selectButton?.classList.add('hidden');
    } else {
      card.classList.remove('selected');
      viewButton?.classList.remove('hidden');
      editButton?.classList.add('hidden');
      removeButton?.classList.add('hidden');
      selectButton?.classList.remove('hidden');
    }
  }

  updateCardSummary(card, variantData) {
    const variantText = card.querySelector('.product-builder__variant');
    if (variantText) {
      variantText.textContent = variantData.title;
      variantText.style.display = 'block';
    }

    const priceContainer = card.querySelector('.product-builder__price');
    if (priceContainer) {
      // The row shows what actually gets added: price x the item's quantity
      // multiplier (metaobject "multiplier" field, defaults to 1).
      const multiplier = parseInt(card.querySelector('.product-builder__input')?.dataset.qtyMultiplier || '1', 10) || 1;
      priceContainer.innerHTML = this.buildPriceHtml(variantData.price * multiplier, (variantData.compareAtPrice || 0) * multiplier, variantData.available);
    }

    const image = card.querySelector('.product-builder__image img');
    if (image && variantData.imageSrc) {
      image.src = variantData.imageSrc;
      image.alt = variantData.imageAlt || variantData.title;
      if (image.hasAttribute('srcset')) {
        image.removeAttribute('srcset');
      }
    }
  }

  resetCardSummary(card) {
    const variantText = card.querySelector('.product-builder__variant');
    if (variantText) {
      const originalTitle = card.dataset.originalVariantTitle || '';
      variantText.textContent = originalTitle;
      if (!originalTitle) {
        variantText.style.display = 'none';
      }
    }

    const priceContainer = card.querySelector('.product-builder__price');
    if (priceContainer && card.dataset.originalPrice) {
      priceContainer.innerHTML = card.dataset.originalPrice;
    }

    const image = card.querySelector('.product-builder__image img');
    if (image) {
      const { originalSrc, originalSrcset, originalAlt } = image.dataset;
      if (originalSrc) {
        image.src = originalSrc;
      }
      if (originalSrcset) {
        image.setAttribute('srcset', originalSrcset);
      }
      if (originalAlt) {
        image.alt = originalAlt;
      }
    }
  }

  getProductVariants(card) {
    if (!card._productVariants) {
      const script = card.querySelector('[data-product-variants]');
      if (script) {
        try {
          card._productVariants = JSON.parse(script.textContent);
        } catch (error) {
          card._productVariants = [];
        }
      } else {
        card._productVariants = [];
      }
    }

    return Array.isArray(card._productVariants) ? card._productVariants : [];
  }

  getVariantById(card, variantId) {
    if (!variantId) return null;
    const variants = this.getProductVariants(card);
    return variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  getSelectedVariantFromPicker(card) {
    const options = card.querySelector('[data-product-builder-options]');
    if (!options) return null;

    const picker = options.querySelector('variant-picker');
    if (!picker) return null;

    const selections = {};

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const optionKey = fieldset.dataset.optionIndex || '';
      const position = parseInt(optionKey.replace('option', ''), 10);
      if (Number.isNaN(position)) return;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) {
        selections[`option${position}`] = checked.value;
      }
    });

    picker.querySelectorAll('select').forEach((select) => {
      const optionKey = select.dataset.optionIndex || select.name;
      const position = parseInt(optionKey?.replace('option', ''), 10);
      if (Number.isNaN(position)) return;
      if (select.value) {
        selections[`option${position}`] = select.value;
      }
    });

    const variants = this.getProductVariants(card);
    return (
      variants.find((variant) => {
        return (!selections.option1 || variant.option1 === selections.option1) &&
               (!selections.option2 || variant.option2 === selections.option2) &&
               (!selections.option3 || variant.option3 === selections.option3);
      }) || null
    );
  }

  normalizeVariantData(variant) {
    if (!variant) return null;

    const title = variant.title === 'Default Title' ? 'Default' : variant.title;
    let imageSrc = '';
    if (variant.featured_image?.src) {
      imageSrc = this.getSizedImageSrc(variant.featured_image.src, 400);
    } else if (variant.image?.src) {
      imageSrc = this.getSizedImageSrc(variant.image.src, 400);
    }
    const imageAlt = variant.featured_image?.alt || variant.image?.alt || title;

    return {
      id: String(variant.id),
      title,
      price: parseInt(variant.price, 10) || 0,
      compareAtPrice: parseInt(variant.compare_at_price, 10) || 0,
      imageSrc,
      imageAlt,
      available: variant.available !== false,
    };
  }

  updateOptionsPreview(card, variant) {
    const normalized = this.normalizeVariantData(variant);
    if (!normalized) return;

    const titleEl = card.querySelector('[data-product-builder-option-title]');
    if (titleEl) {
      if (titleEl.dataset.showVariantTitle !== 'false') {
        titleEl.textContent = normalized.title;
      }
    }

    const priceEl = card.querySelector('[data-product-builder-option-price]');
    if (priceEl) {
      priceEl.innerHTML = this.buildPriceHtml(normalized.price, normalized.compareAtPrice, normalized.available);
    }

    const imageEl = card.querySelector('[data-product-builder-option-image]');
    if (imageEl) {
      if (normalized.imageSrc) {
        imageEl.src = normalized.imageSrc;
        imageEl.alt = normalized.imageAlt || normalized.title;
        if (imageEl.hasAttribute('srcset')) {
          imageEl.removeAttribute('srcset');
        }
        imageEl.classList.remove('hidden');
      } else {
        imageEl.classList.add('hidden');
      }
    }
  }

  updateApplyButtonState(card, variant) {
    const options = card.querySelector('[data-product-builder-options]');
    if (!options) return;

    const applyButton = options.querySelector('[data-bundle-action="apply"]');
    if (!applyButton) return;

    const buttonText = applyButton.querySelector('.btn-text');
    const isAvailable = variant ? variant.available !== false : false;

    if (buttonText && !buttonText.dataset.defaultText) {
      buttonText.dataset.defaultText = buttonText.textContent || 'Add Selection';
    }

    applyButton.disabled = !isAvailable;
    if (buttonText) {
      buttonText.textContent = isAvailable
        ? (buttonText.dataset.defaultText || 'Add Selection')
        : (window.themeVariables?.strings?.soldOut || 'Sold out');
    }
  }

  getSizedImageSrc(src, size) {
    if (!src) return '';
    if (typeof Shopify !== 'undefined' && Shopify.Image && typeof Shopify.Image.getSizedImageUrl === 'function') {
      return Shopify.Image.getSizedImageUrl(src, `${size}x`);
    }

    const match = src.match(/(\.[^.?]+)(\?.*)?$/);
    if (!match) return src;

    const extension = match[1];
    const query = match[2] || '';
    return src.replace(extension + query, `_${size}x${extension}${query}`);
  }

  buildPriceHtml(price, compareAtPrice, available) {
    if (!available) {
      return `<span class="text-sm text-gray-500">${window.themeVariables?.strings?.soldOut || 'Sold out'}</span>`;
    }

    const priceHtml = `<span class="text-sm font-medium text-gray-900">${this.formatMoney(price)}</span>`;

    if (compareAtPrice > price) {
      return `${priceHtml} <span class="text-sm text-gray-500 line-through">${this.formatMoney(compareAtPrice)}</span>`;
    }

    return priceHtml;
  }

  triggerBundlePriceUpdate() {
    const form = this.form || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const items = Array.from(document.querySelectorAll('.product-builder__input:checked')).map((input) => ({
      id: input.value,
      quantity: parseInt(input.dataset.qtyMultiplier || '1', 10) || 1,
      price: parseInt(input.dataset.price || '0', 10) || 0,
    }));

    form.dispatchEvent(new CustomEvent('bundle:change', {
      detail: { items },
      bubbles: true,
    }));
  }

  updateConditionalBlocks(selectedVariantId) {
    if (!this.conditionalBlocks?.length) return;

    const currentVariantId = selectedVariantId || this.getSelectedMainVariantId();
    let shouldClearBundleSelections = false;
    let bundleBuilderIsHidden = false;

    this.conditionalBlocks.forEach((element) => {
      const requiredVariantsRaw = (element.dataset.requiredVariant || '').trim();
      const wasHidden = element.hidden;

      // Check if this element contains the bundle builder or is a total price container
      const isBundleBuilder = element.hasAttribute('data-product-builder') || element.querySelector('[data-product-builder="true"]');
      const isTotalPrice = element.classList.contains('custom-total-price__container');

      if (!requiredVariantsRaw) {
        // If no required variants specified, use default behavior
        // Bundle builder and total price should follow the same visibility rules
        if (isBundleBuilder || isTotalPrice) {
          // For elements without required variants, they inherit visibility from the bundle builder state
          // If the bundle builder is controlled by variants, sync the total price to it
          const bundleBuilderElement = isTotalPrice
            ? document.querySelector('[data-product-builder="true"]')?.closest('[data-required-variant]')
            : null;

          if (bundleBuilderElement && bundleBuilderElement.hasAttribute('data-required-variant')) {
            // Sync visibility with bundle builder
            element.hidden = bundleBuilderElement.hidden;
            if (bundleBuilderElement.hidden) {
              element.setAttribute('aria-hidden', 'true');
            } else {
              element.removeAttribute('aria-hidden');
            }
          } else {
            // No specific requirements, show by default
            element.hidden = false;
            element.removeAttribute('aria-hidden');
          }
        } else {
          element.hidden = false;
          element.removeAttribute('aria-hidden');
        }

        if (isBundleBuilder && element.hidden) {
          bundleBuilderIsHidden = true;
        }
        return;
      }

      const requiredVariantList = requiredVariantsRaw.split(',').map((value) => value.trim()).filter((value) => value.length);
      const shouldBeVisible = !requiredVariantList.length || requiredVariantList.includes(String(currentVariantId));

      // Update visibility
      element.hidden = !shouldBeVisible;
      if (shouldBeVisible) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', 'true');
      }

      // Track bundle builder visibility state
      if (isBundleBuilder) {
        bundleBuilderIsHidden = element.hidden;
        // Clear selections when bundle builder visibility changes
        if (wasHidden !== element.hidden) {
          shouldClearBundleSelections = true;
        }
      }
    });

    // Clear all bundle selections when the bundle builder visibility changes
    if (shouldClearBundleSelections) {
      this.clearAllBundleSelections();
    } else if (bundleBuilderIsHidden) {
      // If bundle is hidden but we haven't cleared yet, force a price update to restore original
      this.updateCustomTotalPrice();
    }
  }

  clearAllBundleSelections() {
    this.cards.forEach((card) => this.clearCardSelection(card));

    // Update prices and state after clearing all selections
    this.triggerBundlePriceUpdate();
    this.updateCustomTotalPrice();
    this.updateAddToCartState();
  }

  clearCardSelection(card) {
    const hiddenInput = card.querySelector('.product-builder__input');
    if (!hiddenInput) return;

    const wasChecked = hiddenInput.checked;

    // Reset the input
    hiddenInput.checked = false;
    hiddenInput.value = card.dataset.originalVariantId || hiddenInput.value;
    hiddenInput.dataset.price = card.dataset.originalInputPrice || hiddenInput.dataset.price;
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Reset card state
    card.dataset.currentVariantId = card.dataset.originalVariantId || '';
    this.resetCardSummary(card);
    this.setCardSelectedState(card, false);

    // Close any open options
    const options = card.querySelector('[data-product-builder-options]');
    if (options && !options.hasAttribute('hidden')) {
      this.closeOptions(card, options);
    }

    // Sync picker with original variant
    if (wasChecked) {
      this.syncOptionsWithCurrentVariant(card);
      const originalVariant = this.getVariantById(card, card.dataset.originalVariantId);
      if (originalVariant) {
        this.updateOptionsPreview(card, originalVariant);
        this.updateApplyButtonState(card, originalVariant);
      } else {
        this.updateApplyButtonState(card, null);
      }
    }
  }

  getCardRequiredFlag(card) {
    const value = card.dataset.required;
    if (!value) return false;
    const normalized = value.toString().trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  getRequiredCards() {
    const reversed = this.isRequirementReversed();
    return this.cards.filter((card) => this.getCardRequiredFlag(card) !== reversed);
  }

  getMainVariantText() {
    const picker = document.querySelector('variant-selects.product-form__variants')
      || document.querySelector('variant-radios.product-form__variants');
    if (picker?.currentVariant?.title) {
      return String(picker.currentVariant.title);
    }

    const scope = this.productInfo || document;
    const parts = [];
    scope
      .querySelectorAll('variant-selects input:checked, variant-selects select, variant-radios input:checked, variant-picker input:checked, variant-picker select')
      .forEach((input) => {
        if (input.closest('.product-builder__card') || input.closest('quick-view')) return;
        if (input.value) parts.push(input.value);
      });
    return parts.join(' / ');
  }

  isRequirementReversed() {
    if (!this.reverseRequiredMatch) return false;
    const variantText = this.getMainVariantText();
    if (!variantText) return false;
    return variantText.toLowerCase().includes(this.reverseRequiredMatch.toLowerCase());
  }

  updateReversedState() {
    if (!this.reverseRequiredMatch) return;

    const reversed = this.isRequirementReversed();
    if (reversed === this.lastReversedState) return;
    this.lastReversedState = reversed;

    let clearedSelection = false;
    this.cards.forEach((card) => {
      if (!this.getCardRequiredFlag(card)) return;
      if (reversed) {
        // Normally-required items are incompatible with the reversed variant:
        // drop any selection and hide the card.
        const hiddenInput = card.querySelector('.product-builder__input');
        if (hiddenInput?.checked) {
          this.clearCardSelection(card);
          clearedSelection = true;
        }
        card.hidden = true;
        card.setAttribute('aria-hidden', 'true');
      } else {
        card.hidden = false;
        card.removeAttribute('aria-hidden');
      }
    });

    this.updateBuilderHeading(reversed);

    if (clearedSelection) {
      this.triggerBundlePriceUpdate();
      this.updateCustomTotalPrice();
    }
    this.updateAddToCartState();
  }

  updateBuilderHeading(reversed) {
    if (!this.reversedHeading) return;
    const heading = this.builderRoot?.closest('.product-builder-block')?.querySelector('.product-builder__heading h3');
    if (!heading) return;

    if (!heading.dataset.originalHtml) {
      heading.dataset.originalHtml = heading.innerHTML;
    }

    if (reversed) {
      const note = heading.querySelector('.product-builder__heading-note');
      heading.textContent = '';
      heading.append(document.createTextNode(`${this.reversedHeading} `));
      if (note) heading.append(note);
    } else {
      heading.innerHTML = heading.dataset.originalHtml;
    }
  }

  isRequirementSatisfied() {
    const requiredCards = this.getRequiredCards();
    if (!requiredCards.length) return true;
    return requiredCards.some((card) => card.querySelector('.product-builder__input')?.checked);
  }

  updateAddToCartState() {
    if (!this.submitButton) return;

    if (this.isRequirementSatisfied()) {
      this.releaseSubmitButton();
      this.hideRequirementMessage();
      return;
    }

    this.lockSubmitButton();
  }

  lockSubmitButton() {
    if (!this.submitButton) return;
    this.submitButton.classList.add('is-disabled-by-bundle');
    this.submitButton.setAttribute('aria-disabled', 'true');

    this.applySubmitButtonWarning();

    if (this.submitButton.dataset.bundleDisabledBySelector === 'true') {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('disabled', 'disabled');
      return;
    }

    const wasDisabled = this.submitButton.disabled || this.submitButton.hasAttribute('disabled');
    this.submitButton.dataset.bundleDisabledBySelector = 'true';
    this.submitButton.dataset.bundleWasDisabled = wasDisabled ? 'true' : 'false';
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('disabled', 'disabled');
  }

  releaseSubmitButton() {
    if (!this.submitButton) return;

    this.submitButton.classList.remove('is-disabled-by-bundle');
    this.submitButton.removeAttribute('aria-disabled');
    this.resetSubmitButtonWarning();

    if (this.submitButton.dataset.bundleDisabledBySelector !== 'true') {
      return;
    }

    const wasDisabled = this.submitButton.dataset.bundleWasDisabled === 'true';
    delete this.submitButton.dataset.bundleDisabledBySelector;
    delete this.submitButton.dataset.bundleWasDisabled;

    if (!wasDisabled && this.isMainProductAvailable()) {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('disabled');
    }
  }

  attachFormGuard() {
    if (!this.form || !this.submitButton) return;

    if (this.form.dataset.bundleGuardAttached === 'true') return;
    this.form.dataset.bundleGuardAttached = 'true';

    this.form.addEventListener('submit', (event) => {
      if (this.isRequirementSatisfied()) {
        this.hideRequirementMessage();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.lockSubmitButton();
      this.showRequirementMessage();
    });

    if (this.submitButton.dataset.bundleGuardAttached === 'true') return;
    this.submitButton.dataset.bundleGuardAttached = 'true';

    this.submitButton.addEventListener('click', (event) => {
      if (this.isRequirementSatisfied()) {
        this.hideRequirementMessage();
        return;
      }

      event.preventDefault();
      this.lockSubmitButton();
      this.showRequirementMessage();
    });
  }

  showRequirementMessage() {
    if (!this.errorMessage) return;
    if (!this.errorMessage.dataset.originalMessage) {
      this.errorMessage.dataset.originalMessage = this.errorMessage.innerHTML;
    }
    this.errorMessage.innerHTML = 'Select at least one required bundle product.';
    this.errorMessage.hidden = false;
    this.errorMessage.classList.remove('hidden');
    this.errorMessage.dataset.bundleMessageActive = 'true';
  }

  hideRequirementMessage() {
    if (!this.errorMessage || this.errorMessage.dataset.bundleMessageActive !== 'true') return;
    if (this.errorMessage.dataset.originalMessage) {
      this.errorMessage.innerHTML = this.errorMessage.dataset.originalMessage;
    }
    this.errorMessage.hidden = true;
    this.errorMessage.classList.add('hidden');
    delete this.errorMessage.dataset.bundleMessageActive;
  }

  isMainProductAvailable() {
    const variantPicker = document.querySelector('variant-selects.product-form__variants');
    if (variantPicker?.currentVariant) {
      return variantPicker.currentVariant.available !== false;
    }

    const mainVariant = document.querySelector('input[name="id"][value]');
    if (mainVariant && mainVariant.disabled) {
      return false;
    }

    return true;
  }

  applySubmitButtonWarning() {
    const warningText = this.isRequirementReversed() && this.reversedButtonText
      ? this.reversedButtonText
      : this.requiredButtonText;
    if (!this.submitButton || !warningText || !this.submitLabel) return;

    if (!this.submitLabel.dataset.bundleOriginalHtml) {
      this.submitLabel.dataset.bundleOriginalHtml = this.submitLabel.innerHTML;
    }

    this.submitLabel.innerHTML = `<span>${warningText}</span>`;
    this.submitLabel.dataset.bundleWarningActive = 'true';
  }

  resetSubmitButtonWarning() {
    if (!this.submitButton || !this.submitLabel || this.submitLabel.dataset.bundleWarningActive !== 'true') return;

    const originalHtml = this.submitLabel.dataset.bundleOriginalHtml;
    if (originalHtml) {
      this.submitLabel.innerHTML = originalHtml;
    }

    delete this.submitLabel.dataset.bundleWarningActive;
  }

  updateCustomTotalPrice() {
    const totalPriceContainer = document.querySelector('.custom-total-price__container');
    const totalPriceElement = document.querySelector('[id^="CustomTotalPrice-"]');
    if (!totalPriceElement || !totalPriceContainer) return;

    // Get settings from data attributes
    const hideUntilBundle = totalPriceContainer.dataset.hideUntilBundle === 'true';
    const includeMainInDiscount = totalPriceContainer.dataset.includeMainDiscount !== 'false';
    const discountPercentage = parseFloat(totalPriceContainer.dataset.discountPercentage || '10');

    const mainPrice = this.getMainProductPrice();
    const builderTotal = Array.from(document.querySelectorAll('.product-builder__input:checked')).reduce((total, input) => {
      const price = parseInt(input.dataset.price || '0', 10) || 0;
      const multiplier = parseInt(input.dataset.qtyMultiplier || '1', 10) || 1;
      return total + price * multiplier;
    }, 0);

    // Calculate discount based on settings
    let discountedAmount;
    if (includeMainInDiscount) {
      // Include main product in discount calculation
      const total = mainPrice + builderTotal;
      discountedAmount = Math.round(total * (1 - discountPercentage / 100));
    } else {
      // Main product not discounted, only bundle items
      const bundleDiscounted = Math.round(builderTotal * (1 - discountPercentage / 100));
      discountedAmount = mainPrice + bundleDiscounted;
    }

    const total = mainPrice + builderTotal;

    // Update the price display
    totalPriceElement.innerHTML = `
      <div class="original-total" style="text-decoration: line-through; color: #6b7280; margin-bottom: 0.25rem;">
        <span class="money">${this.formatMoney(total)}</span>
      </div>
      <div class="kit-discount-line" style="font-size: 0.875rem; color: #059669; margin-bottom: 0.25rem;">
        Kit Discount ${discountPercentage}%
      </div>
      <div class="discounted-total">
        <span class="money">${this.formatMoney(discountedAmount)}</span>
      </div>
    `;

    // Handle hide until bundle selected (after updating price)
    if (hideUntilBundle) {
      if (builderTotal > 0) {
        totalPriceContainer.style.display = '';
      } else {
        totalPriceContainer.style.display = 'none';
      }
    }

    this.updatePrimaryPrice({ builderTotal, total, discounted: discountedAmount });
  }

  updatePrimaryPrice({ builderTotal, total, discounted }) {
    // No longer manipulating the main product price display
    // The bundle total price block handles all pricing display
    return;
  }

  getSelectedMainVariantId() {
    const variantSelects = document.querySelector('variant-selects.product-form__variants');
    if (variantSelects?.currentVariant?.id) {
      return String(variantSelects.currentVariant.id);
    }

    const variantRadios = document.querySelector('variant-radios.product-form__variants');
    if (variantRadios?.currentVariant?.id) {
      return String(variantRadios.currentVariant.id);
    }

    const activeVariantInput = this.form?.querySelector('input[name="id"][value]:not([disabled])');
    if (activeVariantInput) {
      return String(activeVariantInput.value);
    }

    const mainVariantInput = document.querySelector('[data-main-product-variant]');
    if (mainVariantInput?.value) {
      return String(mainVariantInput.value);
    }

    return null;
  }

  getMainProductPrice() {
    const variantPicker = document.querySelector('variant-selects.product-form__variants');
    if (variantPicker?.currentVariant) {
      return variantPicker.currentVariant.price;
    }

    const selectors = [
      '[id*="Price-"][id*="main-product"] .money',
      '.product__price .price .money',
      '.price__regular .money',
      'product-info .price .money',
    ];

    for (const selector of selectors) {
      const candidates = document.querySelectorAll(selector);
      for (const element of candidates) {
        if (element.closest('[id^="CustomTotalPrice-"]') || element.closest('.product-builder__card')) {
          continue;
        }

        const priceText = element.textContent.trim();
        const numeric = parseFloat(priceText.replace(/[^\d.]/g, ''));
        if (!Number.isNaN(numeric)) {
          return Math.round(numeric * 100);
        }
      }
    }

    return 0;
  }

  formatMoney(cents) {
    const value = parseInt(cents, 10) || 0;

    if (typeof theme !== 'undefined' && theme.Currency && typeof theme.Currency.formatMoney === 'function' && window.themeVariables?.settings?.moneyFormat) {
      try {
        return theme.Currency.formatMoney(value, window.themeVariables.settings.moneyFormat);
      } catch (error) {
        console.error('Money formatting failed with theme formatter.', error);
      }
    }

    const locale = document.documentElement.lang || 'en-US';
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100);
  }

  waitForElement(selector, callback) {
    const maxTries = 50;
    let tries = 0;

    const interval = setInterval(() => {
      const element = document.querySelector(selector);
      if (element || tries >= maxTries) {
        clearInterval(interval);
        callback(element || null);
      }
      tries += 1;
    }, 100);
  }
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BundleVariantSelector());
} else {
  new BundleVariantSelector();
}
